/**
 * Toilet fixture texture — the visual "body" of the bottom waste receptacle
 * (the `*-toilet` ports in levels 4/6/10/11). Drawn in the clean white line-art
 * style of the reference art: a two-piece toilet (tank + lid on top, oval seat
 * + bowl + flared pedestal below). The receptacle cone protrudes from the top
 * (the tank lid) as the pipe inlet — see `Port.ts` (`decoration` handling).
 *
 * Front view. ~136 × 140 px. Top-center of the tank lid is the inlet anchor.
 */
import Phaser from 'phaser';
import type { HasTexture } from './core';

export function registerToiletTextures(scene: Phaser.Scene, has: HasTexture): void {
  if (!has('fixture-toilet')) makeToilet(scene, 'fixture-toilet');
}

function makeToilet(scene: Phaser.Scene, key: string): void {
  const W = 136;
  const H = 140;
  const g = scene.add.graphics();

  // Porcelain palette — near-white fills, mid-gray outline, soft shade/highlight.
  const outline = 0x6f6f6f;
  const fill = 0xf6f6f6;
  const shade = 0xdadada;
  const hi = 0xffffff;
  const innerFill = 0xe7e7e7;

  g.lineStyle(3, outline, 1);

  // ---------- Tank body ----------
  g.fillStyle(fill, 1);
  g.fillRoundedRect(34, 16, 68, 52, 9);
  g.strokeRoundedRect(34, 16, 68, 52, 9);
  // tank highlight (left) + shade (right) for a little 3D form
  g.fillStyle(hi, 0.6);
  g.fillRoundedRect(39, 22, 8, 40, 4);
  g.fillStyle(shade, 0.55);
  g.fillRoundedRect(90, 22, 8, 42, 4);

  // ---------- Tank lid (overhangs the tank) ----------
  g.fillStyle(fill, 1);
  g.fillRoundedRect(28, 4, 80, 16, 6);
  g.strokeRoundedRect(28, 4, 80, 16, 6);
  g.fillStyle(hi, 0.5);
  g.fillRoundedRect(33, 7, 70, 4, 2);

  // ---------- Flush handle (left lever) ----------
  g.fillStyle(fill, 1);
  g.fillRoundedRect(16, 28, 22, 9, 4);
  g.strokeRoundedRect(16, 28, 22, 9, 4);

  // ---------- Bowl (tub tapering from the seat down to the pedestal) ----------
  const bowl = bowlPoints();
  g.fillStyle(fill, 1);
  g.fillPoints(bowl, true);
  g.strokePoints(bowl, true);
  // bowl right-side shade
  g.fillStyle(shade, 0.45);
  g.fillEllipse(86, 104, 34, 40);

  // ---------- Pedestal base (flared foot) ----------
  g.fillStyle(fill, 1);
  g.fillRoundedRect(48, 122, 40, 16, 5);
  g.strokeRoundedRect(48, 122, 40, 16, 5);

  // ---------- Seat rim (oval) — drawn over the tank base + bowl top ----------
  g.fillStyle(fill, 1);
  g.fillEllipse(68, 78, 98, 32);
  g.strokeEllipse(68, 78, 98, 32);
  // seat highlight crescent (upper-left)
  g.lineStyle(3, hi, 0.5);
  g.beginPath();
  g.arc(68, 78, 44, Phaser.Math.DEG_TO_RAD * 160, Phaser.Math.DEG_TO_RAD * 250, false);
  g.strokePath();
  g.lineStyle(3, outline, 1);

  // ---------- Bowl opening (inner ellipse) ----------
  g.fillStyle(innerFill, 1);
  g.fillEllipse(68, 79, 66, 18);
  g.lineStyle(2, outline, 1);
  g.strokeEllipse(68, 79, 66, 18);

  g.generateTexture(key, W, H);
  g.destroy();
}

/**
 * Sampled outline of the bowl: a downward-tapering tub. Left and right sides are
 * quadratic curves; the bottom is gently rounded. The top edge is left straight
 * (it sits hidden under the seat ellipse, which is drawn afterward).
 */
function bowlPoints(): Phaser.Geom.Point[] {
  const pts: Phaser.Geom.Point[] = [];
  const quad = (
    x0: number,
    y0: number,
    cx: number,
    cy: number,
    x1: number,
    y1: number,
  ): void => {
    const steps = 16;
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const u = 1 - t;
      pts.push(
        new Phaser.Geom.Point(
          u * u * x0 + 2 * u * t * cx + t * t * x1,
          u * u * y0 + 2 * u * t * cy + t * t * y1,
        ),
      );
    }
  };
  // top-left → down the left side → across the bottom → up the right side
  quad(26, 80, 20, 112, 52, 128); // left wall
  quad(52, 128, 68, 134, 84, 128); // rounded bottom
  quad(84, 128, 116, 112, 110, 80); // right wall
  return pts;
}
